import dayjs from 'dayjs';
import SideSheet from '..';
import { TransactionDetailsProps } from '../../../types';
import './TransactionDetailsAlt.scss';
import {
  capitalize,
  copyToClipboard,
  formatBracketText,
  formatToDollar,
  formatToNaira,
  OverideSelectStyle,
} from '../../../helpers/utils';
import { MdContentCopy } from 'react-icons/md';
import { Select } from '@piggytechng/component-library';

const VERBATIM_KEY = /e-?mail|url|link|address/i;

const TransactionDetailsAlt = ({
  onClose = () => {},
  show,
  data,
  which,
  isDollar,
  runAction,
  withdrawalProcessor,
  setWithdrawalProcessor,
}: TransactionDetailsProps) => {
  const close = () => {
    setWithdrawalProcessor?.('');
    onClose();
  };
  const resolveHeroAmount = () => {
    const raw = which === 'flexnaira' ? data?.value : (data?.amount ?? data?.rawAmount ?? data?.value);

    if (raw === null || raw === undefined || raw === '') return '-----';

    const asString = String(raw).trim();
    if (asString.includes('₦') || (isDollar && asString.includes('$'))) return asString;

    const numeric = Number(asString.replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numeric)) return asString;

    return isDollar ? formatToDollar(numeric) : formatToNaira(numeric);
  };

  const buildCopyAllText = () => {
    const lines: string[] = [`Amount: ${resolveHeroAmount()}`];

    Object.entries(data).forEach(([key, value]) => {
      let val: any = value;

      if (key === 'created_at') {
        val = dayjs(String(value)).format('D MMM YYYY | h:mm A');
      }
      if (key === 'date' && which === 'flexnaira') {
        val = dayjs((value as any) * 1000).format('D MMM YYYY | h:mm A');
      }
      if (key === 'title' && which === 'flexnaira') {
        val = formatBracketText(value as string);
      }
      if (key === 'updated_at') {
        return;
      }
      if (key === 'amount' || key === 'rawAmount' || (which === 'flexnaira' && key === 'value')) {
        return;
      }
      if (key === 'balance' && !isDollar) {
        val = val.toString().includes('₦') ? val : formatToNaira(val);
      }
      if ((key === 'rejection_reason' || key === 'released_at' || key === 'released_by') && value === null) {
        return;
      }

      const label = key === 'id' ? 'Transaction Id' : key.replace('_', ' ');
      lines.push(`${label}: ${value !== null && value !== undefined && value !== '' ? val : '-----'}`);
    });

    return lines.join('\n');
  };

  const handleCopyAll = () => {
    copyToClipboard('All transaction details', buildCopyAllText());
  };

  return (
    <>
      {data && (
        <div>
          <SideSheet
            onClose={close}
            active={show}
            title="Transaction Details"
            actions={
              <div className="transaction-details__actions">
                {which === 'heldusdrequest' && data.status === 'withheld' && (
                  <>
                    <button className="primary" onClick={() => runAction?.('release-usd-request', data)}>
                      Release
                    </button>
                    <button className="secondary" onClick={() => runAction?.('reject-usd-request', data)}>
                      Reject
                    </button>
                  </>
                )}
                {which === 'withdrawal' && (
                  <>
                    {data.status === 'On Hold' && (
                      <button
                        className="primary"
                        onClick={() => runAction?.('send_cash', { ...data, processor: withdrawalProcessor })}
                      >
                        Send Cash
                      </button>
                    )}
                    {data.status !== 'Success' && data.status !== 'Reversed' && (
                      <button className="secondary" onClick={() => runAction?.('check_status', data)}>
                        Check Status
                      </button>
                    )}
                  </>
                )}
                {/* Utility, never the primary action — it must not compete with Send Cash / Release. */}
                <button className="secondary" onClick={handleCopyAll}>
                  <MdContentCopy />
                  Copy All
                </button>
              </div>
            }
          >
            <div className="transaction-details flex--4">
              <div className="transaction-details__hero">
                <h1>{resolveHeroAmount()}</h1>
              </div>
              <div className="details flex--2">
                {Object.entries(data).map(([key, value]) => {
                  let val: any = value;
                  if (key === 'created_at') {
                    val = dayjs(String(value)).format('D MMM YYYY | h:mm A');
                  }
                  if (key === 'date' && which === 'flexnaira') {
                    val = dayjs((value as any) * 1000).format('D MMM YYYY | h:mm A');
                  }
                  if (key === 'title' && which === 'flexnaira') {
                    val = formatBracketText(value as string);
                  }
                  if (key === 'updated_at') {
                    return;
                  }
                  if (key === 'amount' || key === 'rawAmount' || (which === 'flexnaira' && key === 'value')) {
                    return;
                  }
                  if (key === 'balance' && !isDollar) {
                    val = val.toString().includes('₦') ? val : formatToNaira(val);
                  }
                  if (
                    (key === 'rejection_reason' || key === 'released_at' || key === 'released_by') &&
                    value === null
                  ) {
                    return;
                  }
                  // Match case-insensitively so both `reference` and `Reference` get a copy icon.
                  const copyableKeys = ['memo', 'withdrawal id', 'reference', 'userid', 'user_id'];

                  if (copyableKeys.includes(key.toLowerCase())) {
                    const isMemo = key === 'memo';

                    if (isMemo) {
                      return (
                        <div key={key} className="label-cont flex--4 label-cont--full">
                          <label>{key.replace('_', ' ')}</label>
                          <div
                            className="copy memo-copy"
                            onClick={() =>
                              copyToClipboard(capitalize(key), typeof value === 'string' ? value : String(value))
                            }
                          >
                            <p className="full-text">{value ? String(val) : '-----'}</p>
                            {String(val) && <MdContentCopy className="pointer" />}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={key} className="label-cont flex--4">
                        <label>{key.replace('_', ' ')}</label>
                        <div
                          className="copy flex--2"
                          onClick={() =>
                            copyToClipboard(capitalize(key), typeof value === 'string' ? value : String(value))
                          }
                        >
                          <p>{value ? String(val) : '-----'}</p>
                          {String(val) && <MdContentCopy className="pointer" />}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={key} className="label-cont flex--4">
                      <label>{key === 'id' ? 'Transaction Id' : key.replace('_', ' ')}</label>
                      <p className={VERBATIM_KEY.test(key) ? 'value--verbatim' : undefined}>
                        {val !== '' ? val : '-----'}
                      </p>
                    </div>
                  );
                })}
              </div>
              {which === 'withdrawal' && data.status === 'On Hold' && (
                <section className="transaction-details__section">
                  <div className="transaction-details__section-head">
                    <h4>Processor</h4>
                    <p>Choose the provider this payout is routed through before sending cash.</p>
                  </div>
                  <Select
                    {...OverideSelectStyle}
                    labelStyle={{ color: '#666766' }}
                    options={[
                      {
                        value: 'paystack',
                        label: 'Paystack',
                      },
                      {
                        value: 'pocket',
                        label: 'Pocket',
                      },
                    ]}
                    backgroundColor="#fff"
                    outlineColor="#e2e8f0"
                    height="56px"
                    placeholder="Select processor"
                    value={withdrawalProcessor || ''}
                    onChange={(e) => setWithdrawalProcessor?.(e)}
                  />
                </section>
              )}
            </div>
          </SideSheet>
        </div>
      )}
    </>
  );
};
export default TransactionDetailsAlt;
